import React, { useState, useEffect, useCallback, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  AppBar, Toolbar, Typography, Box, Grid, Paper, Chip, IconButton,
  Tooltip, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, TextField, MenuItem,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import RefreshIcon from '@material-ui/icons/Refresh';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import PeopleIcon from '@material-ui/icons/People';
import AssignmentIcon from '@material-ui/icons/Assignment';
import EventIcon from '@material-ui/icons/Event';
import {
  XAxis, YAxis, CartesianGrid, Tooltip as RechartTooltip,
  ResponsiveContainer, LineChart, Line, BarChart, Bar, Cell,
} from 'recharts';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import jsPDF from 'jspdf';
import { useHistory } from 'react-router-dom';
import { EvaluationCategoryData } from '../../../services';
import RouteConstants from '../../../common/RouteConstants';
import Loader from '../../../sharedComponents/Loader/Loader';
import { AppDataContext } from '../../../common/AppContext';
import Helper from '../../../common/Helper';
import CultureInfo from '../../../common/CultureInfo';
import PdfIcon from '../../../sharedComponents/customIcons/PdfIcon';

//  Colour helpers 


const BAR_PALETTE = ['#1d428a', '#2563eb', '#16a34a', '#d97706', '#7c3aed', '#db2777', '#0891b2'];

const scoreChipStyle = (score) => {
  if (score >= 4.0) return { backgroundColor: '#dcfce7', color: '#16a34a' };
  if (score >= 3.0) return { backgroundColor: '#fef9c3', color: '#ca8a04' };
  return { backgroundColor: '#fee2e2', color: '#dc2626' };
};

// build year list 2019-CURR plus 'Custom'
const buildYearList = () => {
  const list = ['Custom'];
  const cur = new Date().getFullYear();
  for (let y = 2019; y <= cur; y++) list.push(`${y}-${y + 1}`);
  return list.reverse();
};

// map season-year "YYYY-YYYY+1" â†’ { start: Date, end: Date }
const yearToDates = (yr) => {
  const [from] = yr.split('-');
  const y = parseInt(from, 10);
  const cur = new Date().getFullYear();
  return {
    start: new Date(y, 0, 1),
    end: parseInt(from, 10) === cur ? new Date() : new Date(y, 11, 31),
  };
};

//  Styles 

const useStyles = makeStyles((theme) => ({
  appbarBackground: {
    backgroundColor: '#ffffff',
    color: '#f50101',
  },
  toolbar: {
    alignItems: 'flex-start',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    overflow: 'visible',
    [theme.breakpoints.down('sm')]: {
      minHeight: 112,
    },
  },
  title: { flexGrow: 1, alignSelf: 'center', },
  boxSeasonCycle: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',    
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    overflow: 'visible',
  },
  // MUI date-picker inputs rendered inside the white AppBar need white text
  datepickerWhite: {
    marginTop: theme.spacing(1),
    '& .MuiInputBase-root': { color: '#000000' },
    '& .MuiInputLabel-root': { color: 'rgba(0, 0, 0, 0.7)' },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(13, 19, 129, 0.5)' },
    '& .MuiSvgIcon-root': { color: '#020aa6' },
    width: 160,
  },
  ddSeasonYear: {
    marginTop: theme.spacing(1),
    '& .MuiInputBase-root': { color: '#000000' },
    '& .MuiInputLabel-root': { color: 'rgba(0, 0, 0, 0.7)' },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(13, 19, 129, 0.5)' },
    '& .MuiSvgIcon-root': { color: '#020aa6' },
    width: 175,
  },
  eventFilter: {
    marginTop: theme.spacing(1),
    width: 320,
    minWidth: 260,
    '& .MuiInputBase-root': { color: '#000000' },
    '& .MuiInputLabel-root': { color: 'rgba(0, 0, 0, 0.7)' },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(13, 19, 129, 0.5)' },
    '& .MuiSvgIcon-root': { color: '#020aa6' },
  },
  content: {
    backgroundColor: '#ffffff',
    minHeight: 'calc(100vh - 64px)',
    padding: theme.spacing(2),
  },
  kpiCard: {
    padding: theme.spacing(2),
    borderRadius: 4,
    backgroundColor: '#e8eff9',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    height: '100%',
  },
  kpiIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  kpiValue: {
    fontSize: '2rem',
    fontWeight: 700,
    lineHeight: 1,
  },
  kpiLabel: {
    fontSize: '0.78rem',
    color: '#64748b',
    marginTop: 4,
  },
  panel: {
    borderRadius: 4,
    height: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: theme.palette.primary.main,
    minHeight: 34,
    padding: '0 10px',
  },
  panelHeaderTitle: {
    color: theme.palette.background.paper,
    fontWeight: 600,
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    lineHeight: 1.2,
  },
  panelBody: {
    padding: theme.spacing(2),
    flex: 1,
  },
  widgetRow: {
    marginTop: theme.spacing(3),
  },
  tableHead: {
    '& th': {
      fontWeight: 700,
      fontSize: '0.72rem',
      color: '#475569',
      backgroundColor: '#f8fafc',
      padding: '8px 10px',
      borderBottom: '2px solid #e2e8f0',
      whiteSpace: 'nowrap',
    },
  },
  tableRow: {
    cursor: 'pointer',
    '&:hover': { backgroundColor: '#f1f5f9' },
    '& td': {
      fontSize: '0.78rem',
      padding: '7px 10px',
      borderBottom: '1px solid #f1f5f9',
    },
  },
  emptyCell: {
    textAlign: 'center',
    color: '#94a3b8',
    padding: theme.spacing(3),
    fontSize: '0.82rem',
  },
}));

//  Main Component 

const NewScoutingDashboard = () => {
  const classes = useStyles();
  const history = useHistory();

  const {
    savedFromDate, savedToDate, savedSeasonYear,
    updatedDashboardFromDateState, updatedDashboardToDateState,
    updatedDashboardSeasonYearState, updateDashboardDatesState,
    evaluationEvents, getUpdatedEvaluationEvents,
  } = useContext(AppDataContext);

  //Season / date state (mirrors DashboardContainer) 
  const [seasonYearList] = useState(buildYearList);
  const [selectedSeasonYear, setSelectedSeasonYear] = useState(savedSeasonYear || '');
  const [seasonStartDate, setSeasonStartDate] = useState(savedFromDate || null);
  const [seasonEndDate, setSeasonEndDate] = useState(savedToDate || null);
  const [seasonStartError, setSeasonStartError] = useState(null);
  const [seasonEndError, setSeasonEndError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!evaluationEvents || evaluationEvents.length === 0) {
      getUpdatedEvaluationEvents();
    }
  }, []); 

  // Seed default year on first mount
  useEffect(() => {
    if (!selectedSeasonYear) {
      const defaultYear = seasonYearList.find(y => y !== 'Custom') || '';
      setSelectedSeasonYear(defaultYear);
      if (defaultYear) applySeasonYearDates(defaultYear);
    }
  }, []);

  const applySeasonYearDates = (yr) => {
    if (!yr || yr === 'Custom') return;
    const { start, end } = yearToDates(yr);
    setSeasonStartDate(Helper.FormatDateToLocalTimeZone(start));
    setSeasonEndDate(Helper.FormatDateToLocalTimeZone(end));
    updatedDashboardFromDateState(Helper.FormatDateToLocalTimeZone(start));
    updatedDashboardToDateState(Helper.FormatDateToLocalTimeZone(end));
  };

  //  Persist to AppDataContext whenever dates/year change 
  useEffect(() => {
    if (seasonStartDate && seasonEndDate) {
      updateDashboardDatesState(seasonStartDate, seasonEndDate, selectedSeasonYear);
    }
  }, [seasonStartDate, seasonEndDate, selectedSeasonYear]);

  //  Load dashboard data 
  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const start = seasonStartDate ? Helper.FormatDate(seasonStartDate) : null;
      const end   = seasonEndDate   ? Helper.FormatDate(seasonEndDate)   : null;
      const eventID = selectedEvent?.id || null;
      const result = await EvaluationCategoryData.getScoutingDashboard(start, end, eventID);
      setData(result);
    } catch (err) {
      console.error('New Scouting dashboard load failed', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [seasonStartDate, seasonEndDate, selectedEvent]);

  // Auto-reload when dates change
  useEffect(() => {
    if (seasonStartDate && seasonEndDate) load();
  }, [seasonStartDate, seasonEndDate, selectedEvent]);

  //  Derived / memoised chart data 

  const timelineData = (data?.evaluationTimeline || []).map(t => ({ date: t.date, count: t.count }));

  const handleProspectDrilldown = (officialUID) =>
    history.push({ pathname: RouteConstants.ADMIN_EVALUATION_SEARCH, state: { officialUID } });

  const handleExportPdf = () => {
    if (!data) {
      setError('No dashboard data available to export.');
      return;
    }

    try {
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const margin = 40;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const contentWidth = pageWidth - (margin * 2);
      const lineHeight = 14;
      let y = 36;

      const ensureSpace = (requiredHeight = 24) => {
        if (y + requiredHeight > pageHeight - margin) {
          doc.addPage();
          y = 36;
        }
      };

      const addSectionTitle = (title) => {
        ensureSpace(30);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text(title, margin, y);
        y += 16;
      };

      const addLines = (lines) => {
        lines.forEach((line) => {
          const wrapped = doc.splitTextToSize(line, contentWidth);
          ensureSpace(wrapped.length * lineHeight + 2);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          doc.text(wrapped, margin, y);
          y += wrapped.length * lineHeight;
        });
        y += 8;
      };

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.text('Scouting Dashboard Export', margin, y);
      y += 20;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`Season Year: ${selectedSeasonYear || 'Custom'}`, margin, y);
      y += 14;
      doc.text(`Date Range: ${seasonStartDate ? Helper.FormatDate(seasonStartDate) : 'N/A'} to ${seasonEndDate ? Helper.FormatDate(seasonEndDate) : 'N/A'}`, margin, y);
      y += 20;

      addSectionTitle('Total evaluations and Avg scores');
      addLines(
        (data?.scoutProductivity || []).length > 0
          ? (data.scoutProductivity || []).map((s) =>
            `${s.scoutName || 'N/A'} | Total: ${s.totalEvaluations || 0} | Avg Score: ${parseFloat(s.averageScoreGiven || 0).toFixed(2)}`
          )
          : ['No data']
      );

      addSectionTitle('Event Coverage');
      addLines(
        (data?.eventCoverage || []).length > 0
          ? (data.eventCoverage || []).map((ev) =>
            `${ev.eventName || 'N/A'} | Total: ${ev.evaluationCount || 0} | Prospects: ${ev.uniqueProspects || 0} | Scouts: ${ev.uniqueScouts || 0}`
          )
          : ['No data']
      );

      addSectionTitle('Top 10 Prospects');
      addLines(
        (data?.topProspects || []).length > 0
          ? (data.topProspects || []).map((p, idx) =>
            `${idx + 1}. ${p.prospectName || 'N/A'} | Score: ${parseFloat(p.weightedScore || 0).toFixed(2)} | Evals: ${p.evaluationCount || 0} | Scouts: ${p.scoutCount || 0}`
          )
          : ['No data']
      );

      addSectionTitle('Category wise Averages');
      addLines(
        (data?.categoryAverages || []).length > 0
          ? (data.categoryAverages || []).map((c) =>
            `${c.categoryName || 'N/A'} | Average: ${parseFloat(c.averageScore || 0).toFixed(2)}/5`
          )
          : ['No data']
      );

      addSectionTitle('Most-Flagged Competencies');
      addLines(
        (data?.topCompetencies || []).length > 0
          ? (data.topCompetencies || []).slice(0, 12).map((c) =>
            `${c.competencyName || 'N/A'} (${c.categoryName || 'N/A'})`
          )
          : ['No data']
      );

      addSectionTitle('Evaluation Activity Timeline');
      addLines(
        timelineData.length > 0
          ? timelineData.map((t) => `${t.date}: ${t.count}`)
          : ['No data']
      );

      const fileDate = new Date().toISOString().slice(0, 10);
      doc.save(`scouting-dashboard-${fileDate}.pdf`);
    } catch (pdfError) {
      console.error('PDF export failed', pdfError);
      setError('Failed to export dashboard PDF. Please try again.');
    }
  };

  return (
    <div>
      <Loader isLoading={isLoading} />

      <AppBar position="static" className={classes.appbarBackground}>
        <Toolbar className={classes.toolbar}>
          <Typography variant="h6" className={classes.title}>
            New Scouting Dashboard
          </Typography>

          <Box className={classes.boxSeasonCycle}>
            <Autocomplete
              className={classes.eventFilter}
              value={selectedEvent}
              options={evaluationEvents || []}
              getOptionLabel={(option) =>
                option.eventName
                  ? `${option.eventName}${option.eventFrom ? ` (${option.eventFrom}${option.eventTo ? ` — ${option.eventTo}` : ''})` : ''}`
                  : ''
              }
              getOptionSelected={(option, value) => option.id === value.id}
              onChange={(e, newValue) => setSelectedEvent(newValue)}
              size="small"
              fullWidth
              renderInput={(params) => (
                <TextField {...params} variant="outlined" label="Scout Event" placeholder="Select event..." />
              )}
            />

            <TextField
              className={classes.ddSeasonYear}
              select
              label="Season Year"
              value={selectedSeasonYear}
              onChange={(e) => {
                const yr = e.target.value;
                setSelectedSeasonYear(yr);
                updatedDashboardSeasonYearState(yr);
                if (yr && yr !== 'Custom') applySeasonYearDates(yr);
              }}
              size="small"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            >
              {seasonYearList.map(yr => (
                <MenuItem key={yr} value={yr}>{yr}</MenuItem>
              ))}
            </TextField>

            <MuiPickersUtilsProvider utils={MomentUtils}>
              <DatePicker
                label="Start Date"
                inputVariant="outlined"
                views={['year', 'month', 'date']}
                size="small"
                format={CultureInfo.GetCultureInfo().DateFormat}
                value={seasonStartDate}
                className={classes.datepickerWhite}
                onChange={(val) => {
                  const d = val != null ? Helper.FormatDateToLocalTimeZone(val) : null;
                  setSeasonStartDate(d);
                  setSeasonEndDate(null);
                  setSeasonEndError('Season End Date is required.');
                  updatedDashboardFromDateState(d);
                  updatedDashboardToDateState(null);
                  setSelectedSeasonYear('Custom');
                }}
                error={!!seasonStartError}
                helperText={seasonStartError}
                InputLabelProps={{ shrink: true }}
              />
            </MuiPickersUtilsProvider>

            <MuiPickersUtilsProvider utils={MomentUtils}>
              <DatePicker
                label="End Date"
                inputVariant="outlined"
                views={['year', 'month', 'date']}
                size="small"
                format={CultureInfo.GetCultureInfo().DateFormat}
                value={seasonEndDate}
                className={classes.datepickerWhite}
                minDate={seasonStartDate}
                minDateMessage="End Date must be after Start Date"
                onChange={(val) => {
                  const d = val != null ? Helper.FormatDateToLocalTimeZone(val) : null;
                  setSeasonEndDate(d);
                  updatedDashboardToDateState(d);
                  setSeasonEndError(d ? null : 'Season End Date is required');
                  setSelectedSeasonYear('Custom');
                }}
                error={!!seasonEndError}
                InputLabelProps={{ shrink: true }}
              />
            </MuiPickersUtilsProvider>

            <Tooltip title="Refresh">
              <IconButton size="small" onClick={load} style={{ color: '#020aa6' }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export PDF">
              <IconButton size="small" color="secondary" onClick={handleExportPdf}>
                <PdfIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      <Box className={classes.content}>
        {error && (
          <Box mb={2} p={2} style={{ backgroundColor: '#fee2e2', borderRadius: 8, color: '#dc2626', fontSize: '0.82rem' }}>
            {error}
          </Box>
        )}

        <Grid container spacing={2} style={{marginBottom:'48px'}}>
          {[
            {
              label: 'Total Events',
              value: data?.summary?.totalEvents ?? 0,
              icon: <EventIcon style={{ color: '#1d428a', fontSize: 26 }} />,
              iconBg: '#eff6ff',
            },
            {
              label: 'Unique Prospects',
              value: data?.summary?.uniqueProspects ?? 0,
              icon: <PeopleIcon style={{ color: '#16a34a', fontSize: 26 }} />,
              iconBg: '#f0fdf4',
            },
            {
              label: 'Total Evaluations',
              value: data?.summary?.totalEvaluations ?? 0,
              icon: <AssignmentIcon style={{ color: '#d97706', fontSize: 26 }} />,
              iconBg: '#fffbeb',
            },
          ].map((kpi) => (
            <Grid item xs={12} sm={4} key={kpi.label}>
              <Paper className={classes.kpiCard} elevation={0} variant="outlined">
                <Box className={classes.kpiIcon} style={{ backgroundColor: kpi.iconBg }}>
                  {kpi.icon}
                </Box>
                <Box>
                  <Typography className={classes.kpiValue}>
                    {isLoading ? '—' : kpi.value.toLocaleString()}
                  </Typography>
                  <Typography className={classes.kpiLabel}>{kpi.label}</Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2} className={classes.widgetRow}>
          <Grid item xs={12} md={6}>
            <Paper className={classes.panel} elevation={3}>
              <Box className={classes.panelHeader}>
                <Typography className={classes.panelHeaderTitle}>Total evaluations &amp; Avg scores</Typography>
              </Box>
              <Box className={classes.panelBody}>
                <TableContainer style={{ maxHeight: 240 }}>
                  <Table size="small" stickyHeader>
                    <TableHead className={classes.tableHead}>
                      <TableRow>
                        <TableCell>Scout</TableCell>
                        <TableCell align="center">Total</TableCell>
                        <TableCell align="center">Avg Score</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(data?.scoutProductivity || []).length === 0 ? (
                        <TableRow><TableCell colSpan={3} className={classes.emptyCell}>No data</TableCell></TableRow>
                      ) : (
                        (data?.scoutProductivity || []).map(s => (
                          <TableRow key={s.scoutID} className={classes.tableRow}>
                            <TableCell><Typography style={{ fontSize: '0.78rem', fontWeight: 600 }}>{s.scoutName || '0'}</Typography></TableCell>
                            <TableCell align="center">
                              <Chip label={s.totalEvaluations} size="small" style={{ backgroundColor: '#dcfce7', color: '#16a34a', fontWeight: 700, fontSize: '0.7rem', height: 20 }} />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={parseFloat(s.averageScoreGiven || 0).toFixed(2)}
                                size="small"
                                style={{ ...scoreChipStyle(s.averageScoreGiven), fontWeight: 700, fontSize: '0.7rem', height: 20 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper className={classes.panel} elevation={3}>
              <Box className={classes.panelHeader}>
                <Typography className={classes.panelHeaderTitle}>Event Coverage</Typography>
              </Box>
              <Box className={classes.panelBody}>
                <TableContainer style={{ maxHeight: 200 }}>
                  <Table size="small" stickyHeader>
                    <TableHead className={classes.tableHead}>
                      <TableRow>
                        <TableCell>Event</TableCell>
                        <TableCell align="center">Total</TableCell>
                        <TableCell align="center">Unique Prospects</TableCell>
                        <TableCell align="center">Scouts</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(data?.eventCoverage || []).length === 0
                        ? <TableRow><TableCell colSpan={7} className={classes.emptyCell}>No event data</TableCell></TableRow>
                        : (data?.eventCoverage || []).map(ev => (
                          <TableRow key={ev.eventID} className={classes.tableRow}>
                            <TableCell>
                              <Typography style={{ fontSize: '0.78rem', fontWeight: 600 }}>{ev.eventName}</Typography>
                              {ev.location && <Typography style={{ fontSize: '0.66rem', color: '#94a3b8' }}>{ev.location}</Typography>}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={ev.submittedCount}
                                size="small"
                                style={{ backgroundColor: '#dcfce7', color: '#16a34a', fontWeight: 700, fontSize: '0.68rem', height: 20 }}
                              />
                            </TableCell>
                            <TableCell align="center">{ev.uniqueProspects}</TableCell>
                            <TableCell align="center">{ev.uniqueScouts}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={2} className={classes.widgetRow}>
          <Grid item xs={12} md={4}>
            <Paper className={classes.panel} elevation={3} style={{ height: '360px' }}>
              <Box className={classes.panelHeader}>
                <Typography className={classes.panelHeaderTitle}>Top 10 Prospects</Typography>
                <TrendingUpIcon style={{ color: '#ffffff', fontSize: 16 }} />
              </Box>
              <Box className={classes.panelBody}>
                <TableContainer style={{ maxHeight: '280px', overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead className={classes.tableHead}>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Prospect</TableCell>
                        <TableCell align="center">Score</TableCell>
                        <TableCell align="center">Evals</TableCell>
                        <TableCell align="center">Scouts</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(data?.topProspects || []).length === 0
                        ? <TableRow><TableCell colSpan={5} className={classes.emptyCell}>No data</TableCell></TableRow>
                        : (data?.topProspects || []).map((p, i) => (
                          <TableRow key={p.officialUID} className={classes.tableRow} onClick={() => handleProspectDrilldown(p.officialUID)}>
                            <TableCell style={{ color: '#94a3b8', fontWeight: 600 }}>{i + 1}</TableCell>
                            <TableCell>
                              <Typography style={{ fontSize: '0.78rem', fontWeight: 600 }}>{p.prospectName}</Typography>
                              <Typography style={{ fontSize: '0.68rem', color: '#94a3b8' }}>{p.lastEventName}</Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={parseFloat(p.weightedScore || 0).toFixed(2)} size="small"
                                style={{ ...scoreChipStyle(p.weightedScore), fontWeight: 700, fontSize: '0.7rem', height: 20 }} />
                            </TableCell>
                            <TableCell align="center">{p.evaluationCount}</TableCell>
                            <TableCell align="center">{p.scoutCount}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper className={classes.panel} elevation={3} style={{ height: '360px' }}>
              <Box className={classes.panelHeader}>
                <Typography className={classes.panelHeaderTitle}>Category wise Averages</Typography>
              </Box>
              <Box className={classes.panelBody}>
                {(data?.categoryAverages || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                      data={(data?.categoryAverages || []).map((c) => ({
                        ...c,
                        averageScoreValue: parseFloat(c.averageScore || 0),
                      }))}
                      margin={{ top: 8, right: 8, left: -8, bottom: 44 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis
                        dataKey="categoryName"
                        interval={0}
                        angle={-28}
                        textAnchor="end"
                        tick={{ fontSize: 10, fill: '#475569' }}
                      />
                      <YAxis
                        domain={[0, 5]}
                        allowDecimals={false}
                        tick={{ fontSize: 10, fill: '#475569' }}
                      />
                      <RechartTooltip
                        formatter={(value) => [`${Number(value).toFixed(2)}/5`, 'Average Score']}
                      />
                      <Bar dataKey="averageScoreValue" radius={[8, 8, 0, 0]}>
                        {(data?.categoryAverages || []).map((c, idx) => (
                          <Cell key={c.categoryID} fill={BAR_PALETTE[idx % BAR_PALETTE.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography className={classes.emptyCell}>No category data yet</Typography>
                )}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper className={classes.panel} elevation={3} style={{ height: '360px' }}>
              <Box className={classes.panelHeader}>
                <Typography className={classes.panelHeaderTitle}>Most-Flagged Competencies</Typography>
              </Box>
              <Box className={classes.panelBody} style={{ overflow: 'auto', flex: 1 }}>
                {(data?.topCompetencies || []).length === 0
                  ? <Typography className={classes.emptyCell}>None yet</Typography>
                  : (data?.topCompetencies || []).slice(0, 12).map(c => (
                    <Chip
                      key={c.competencyID}
                      label={`${c.competencyName || 'N/A'}${c.categoryName ? ` (${c.categoryName})` : ''}`}
                      size="small"
                      variant="outlined"
                      style={{ margin: '0 6px 6px 0', fontSize: '0.72rem', height: 24, fontWeight: 600, backgroundColor: '#f8fafc' }}
                    />
                  ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={2} className={classes.widgetRow}>
          <Grid item xs={12} md={7}>
            <Paper className={classes.panel} elevation={3}>
              <Box className={classes.panelHeader}>
                <Typography className={classes.panelHeaderTitle}>Evaluation Activity Timeline</Typography>
              </Box>
              <Box className={classes.panelBody}>
                {timelineData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={timelineData} margin={{ top: 4, right: 8, bottom: 30, left: -20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 8, fill: '#555' }} angle={-35} textAnchor="end"
                        interval={Math.max(0, Math.floor(timelineData.length / 8) - 1)} />
                      <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
                      <RechartTooltip />
                      <Line type="monotone" dataKey="count" name="Evaluations" stroke="#1d428a" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <Typography className={classes.emptyCell}>No timeline data</Typography>}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
};

export default NewScoutingDashboard;
